import { openDB } from 'idb';

const DB_NAME = 'tennisteam-offline';
const QUEUE_STORE = 'mutationQueue';
const DB_VERSION = 2; // Bumped to add queue store

let dbPromise = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        // Stores from v1
        if (!db.objectStoreNames.contains('teamData')) {
          db.createObjectStore('teamData');
        }
        if (!db.objectStoreNames.contains('translations')) {
          db.createObjectStore('translations');
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta');
        }
        // New in v2: mutation queue
        if (!db.objectStoreNames.contains(QUEUE_STORE)) {
          const store = db.createObjectStore(QUEUE_STORE, {
            keyPath: 'id',
            autoIncrement: true,
          });
          store.createIndex('status', 'status');
          store.createIndex('createdAt', 'createdAt');
        }
      },
    });
  }
  return dbPromise;
}

// Re-export getDB so offlineCache.js can share the same DB instance
export { getDB };

/**
 * Add a mutation to the offline queue
 * @param {Object} mutation - { table, operation, data, matchKey? }
 *   table: Supabase table name
 *   operation: 'insert' | 'update' | 'upsert' | 'delete'
 *   data: The payload to send
 *   matchKey: Optional key fields for deduplication (e.g., { match_id, player_id })
 * @returns {number} The queue entry ID
 */
export async function addToQueue(mutation) {
  try {
    const db = await getDB();
    const entry = {
      ...mutation,
      status: 'pending',
      createdAt: Date.now(),
      retries: 0,
    };
    const id = await db.add(QUEUE_STORE, entry);
    return id;
  } catch (err) {
    console.error('Failed to add mutation to queue:', err);
    throw err;
  }
}

/**
 * Get all pending mutations in queue order
 * @returns {Array}
 */
export async function getPendingMutations() {
  try {
    const db = await getDB();
    const all = await db.getAllFromIndex(QUEUE_STORE, 'status', 'pending');
    return all.sort((a, b) => a.createdAt - b.createdAt);
  } catch (err) {
    console.error('Failed to get pending mutations:', err);
    return [];
  }
}

/**
 * Get the count of pending mutations
 * @returns {number}
 */
export async function getPendingCount() {
  try {
    const db = await getDB();
    const count = await db.countFromIndex(QUEUE_STORE, 'status', 'pending');
    return count;
  } catch (err) {
    return 0;
  }
}

/**
 * Mark a mutation as completed (or failed)
 * @param {number} id
 * @param {'completed'|'failed'} status
 * @param {string} [errorMessage]
 */
export async function updateMutationStatus(id, status, errorMessage) {
  try {
    const db = await getDB();
    const entry = await db.get(QUEUE_STORE, id);
    if (!entry) return;

    entry.status = status;
    entry.completedAt = Date.now();
    if (errorMessage) entry.error = errorMessage;
    if (status === 'failed') entry.retries = (entry.retries || 0) + 1;

    await db.put(QUEUE_STORE, entry);
  } catch (err) {
    console.error('Failed to update mutation status:', err);
  }
}

/**
 * Process all pending mutations by executing them against Supabase
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {function} [onProgress] - callback(processed, total)
 * @returns {{ success: number, failed: number }}
 */
export async function processQueue(supabase, onProgress) {
  const pending = await getPendingMutations();
  if (pending.length === 0) return { success: 0, failed: 0 };

  let success = 0;
  let failed = 0;

  for (let i = 0; i < pending.length; i++) {
    const mutation = pending[i];

    try {
      let query = supabase.from(mutation.table);

      switch (mutation.operation) {
        case 'insert':
          query = query.insert(mutation.data);
          break;
        case 'update':
          query = query.update(mutation.data).match(mutation.matchKey);
          break;
        case 'upsert':
          query = query.upsert(mutation.data);
          break;
        case 'delete':
          query = query.delete().match(mutation.matchKey);
          break;
        default:
          throw new Error(`Unknown operation: ${mutation.operation}`);
      }

      const { error } = await query;
      if (error) throw error;

      await updateMutationStatus(mutation.id, 'completed');
      success++;
    } catch (err) {
      console.error(`Failed to process mutation #${mutation.id}:`, err);
      await updateMutationStatus(mutation.id, 'failed', err.message);
      failed++;
    }

    if (onProgress) {
      onProgress(i + 1, pending.length);
    }
  }

  return { success, failed };
}

/**
 * Clear all completed/failed mutations from the queue
 */
export async function clearProcessedMutations() {
  try {
    const db = await getDB();
    const tx = db.transaction(QUEUE_STORE, 'readwrite');
    const store = tx.objectStore(QUEUE_STORE);
    const all = await store.getAll();

    for (const entry of all) {
      if (entry.status === 'completed' || entry.status === 'failed') {
        await store.delete(entry.id);
      }
    }

    await tx.done;
  } catch (err) {
    console.error('Failed to clear processed mutations:', err);
  }
}

/**
 * Clear the entire mutation queue
 */
export async function clearQueue() {
  try {
    const db = await getDB();
    await db.clear(QUEUE_STORE);
  } catch (err) {
    console.error('Failed to clear queue:', err);
  }
}
